import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Empty, Space, Table, Tooltip } from 'antd';
import { listMasters } from '../../services/priceSheetsApi';
import { useAuth } from '../../contexts/AuthContext';
import { API_BASE_URL, PRICE_SHEETS_WIDGET_PREVIEW_URL } from '../../config';
import UploadMasterModal from './UploadMasterModal';
import VersionHistoryModal from './VersionHistoryModal';

// Preview opens the real widget (CustomPriceSheetsWidget) against the
// account's own live-priced sheet — see main.js's fromPreviewQueryParams.
// Dev-only: the widget has no hosted URL yet (architecture Deferred list).
function buildPreviewUrl(sheetKey, apiKey) {
  const params = new URLSearchParams({
    api_endpoint: `${API_BASE_URL}/api`,
    api_key: apiKey || '',
    sheet_key: sheetKey,
  });
  return `${PRICE_SHEETS_WIDGET_PREVIEW_URL}/?${params.toString()}`;
}

const MastersListPanel = () => {
  const { account } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [uploadOpen, setUploadOpen] = useState(false);
  const [historySheet, setHistorySheet] = useState(null); // { sheetKey, displayName }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listMasters();
      setRows(data);
      setError('');
    } catch (err) {
      setError(err.message || 'Could not load price sheets');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div>
      <Space style={{ marginBottom: 16 }}>
        <Button type="primary" onClick={() => setUploadOpen(true)}>
          Upload Price Sheet
        </Button>
      </Space>
      {error && <Alert type="error" message={error} style={{ marginBottom: 16 }} />}
      <Table
        rowKey="master_id"
        loading={loading}
        dataSource={rows}
        locale={{ emptyText: <Empty description="No price sheets uploaded yet" /> }}
        columns={[
          { title: 'Display Name', dataIndex: 'display_name' },
          { title: 'Sheet Key', dataIndex: 'sheet_key' },
          { title: 'Current Version', dataIndex: 'current_version_number', render: (v) => (v ? `v${v}` : '—') },
          {
            title: 'Last Uploaded',
            dataIndex: 'uploaded_at',
            render: (v) => (v ? new Date(v).toLocaleString() : '—'),
          },
          {
            title: '',
            key: 'actions',
            render: (_, row) => (
              <Space>
                <Tooltip title={row.current_version_number ? '' : 'Nothing uploaded yet'}>
                  <a
                    href={row.current_version_number ? buildPreviewUrl(row.sheet_key, account?.api_key) : undefined}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-disabled={!row.current_version_number}
                    style={!row.current_version_number ? { color: 'rgba(0,0,0,0.25)', pointerEvents: 'none' } : undefined}
                  >
                    Preview
                  </a>
                </Tooltip>
                <Button type="link" onClick={() => setHistorySheet({ sheetKey: row.sheet_key, displayName: row.display_name })}>
                  Version History
                </Button>
              </Space>
            ),
          },
        ]}
      />
      <UploadMasterModal
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        onSaved={() => {
          setUploadOpen(false);
          load();
        }}
      />
      <VersionHistoryModal
        sheetKey={historySheet?.sheetKey}
        displayName={historySheet?.displayName}
        onClose={() => setHistorySheet(null)}
        onChanged={load}
      />
    </div>
  );
};

export default MastersListPanel;
